// MarqueeTicker.jsx
// Rotating message ticker. Cycles through enabled messages, each shown for
// its configured durationSec, with a fade transition. Trailing emojis on a
// message get a per-emoji animation (rattle / flip / sparkle / wiggle).
//
// Messages are either { type: "text" } (default) or { type: "card", card: {…} }.
// Card messages render via MarqueeCard with the same fade rotation.
//
// Used by: Main, StartingSoon, Break, Outro, ScreenShare bottom-bar variants.
import { useEffect, useRef, useState } from "react";
import MarqueeCard from "./MarqueeCard";

const CARD_MIN_DURATION_SEC = 9;

const EMOJI_RE = /([\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\uFE0F]+)$/u;

const EMOJI_ANIMS = {
  "🫙": "jopEmojiRattle 1.2s ease-in-out infinite",
  "🥞": "jopEmojiFlip 4s ease-in-out infinite",
  "✨": "jopEmojiSparkle 2.8s ease-in-out infinite",
};
const DEFAULT_ANIM = "jopEmojiWiggle 3.2s ease-in-out infinite";

function AnimatedMessage({ text }) {
  const match = text.match(EMOJI_RE);
  if (!match) return <span>{text}</span>;
  const emoji = match[0];
  const mainText = text.slice(0, text.length - emoji.length);
  const anim = EMOJI_ANIMS[emoji] || DEFAULT_ANIM;
  return (
    <>
      <span>{mainText}</span>
      <span style={{ display: "inline-block", animation: anim, transformOrigin: "center bottom" }}>
        {emoji}
      </span>
    </>
  );
}

function isCard(m) {
  return m?.type === "card";
}

function effectiveDurationMs(m) {
  const sec = m?.durationSec || (isCard(m) ? CARD_MIN_DURATION_SEC : 6);
  if (isCard(m)) return Math.max(sec, CARD_MIN_DURATION_SEC) * 1000;
  return sec * 1000;
}

function isMessagePlayable(m) {
  if (!m || m.enabled === false) return false;
  if (isCard(m)) return true; // cards don't require a `text` body
  return !!m.text;
}

/**
 * @param {Array<{text?: string, enabled?: boolean, durationSec?: number, type?: "text"|"card", card?: object}>} messages
 * @param {string|number} fontSize - CSS font-size value (e.g. "2.1rem")
 * @param {string} color - CSS color
 * @param {number} fadeMs - fade transition duration in ms
 * @param {boolean} cardCompact - render cards in compact (slim header) mode
 */
export default function MarqueeTicker({
  messages = [],
  fontSize = "2.1rem",
  color = "white",
  fadeMs = 500,
  cardCompact = false,
}) {
  const active = (messages || []).filter(isMessagePlayable);
  const [idx, setIdx] = useState(0);
  const [vis, setVis] = useState(true);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (active.length === 0) return;
    const duration = effectiveDurationMs(active[idx % active.length]);
    const t = setTimeout(() => {
      setVis(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % (activeRef.current.length || 1));
        setVis(true);
      }, fadeMs);
    }, duration);
    return () => clearTimeout(t);
  }, [idx, active.length, fadeMs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (active.length === 0) return null;
  const msg = active[idx % active.length];

  return (
    <div style={{ opacity: vis ? 1 : 0, transition: `opacity ${fadeMs}ms ease`, width: "100%" }}>
      {isCard(msg) ? (
        <MarqueeCard msg={msg} fontSize={fontSize} compact={cardCompact} />
      ) : (
        <div style={{ color, fontWeight: 800, fontSize, lineHeight: 1.1 }}>
          <AnimatedMessage text={msg.text} />
        </div>
      )}
    </div>
  );
}
